import { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Coffee, Sparkles } from 'lucide-react';
import { cn } from '@/utils/cn';
import DonateModal from './DonateModal';

type BehaviorType = 'escaping' | 'shrinking' | 'reverse' | 'dramatic' | 'guiltTrip' | 'enthusiastic';

const BEHAVIORS: BehaviorType[] = ['escaping', 'shrinking', 'reverse', 'dramatic', 'guiltTrip', 'enthusiastic'];

const getRandomBehavior = (): BehaviorType => {
  return BEHAVIORS[Math.floor(Math.random() * BEHAVIORS.length)];
};

const BEHAVIOR_TEXTS: Record<BehaviorType, string[]> = {
  escaping: ['Spenden', 'Nope!', 'Fast!', 'Okay...'],
  shrinking: ['Spenden', 'Klein!', 'Winzig!', 'Fein...'],
  reverse: ['NICHT!', 'NEIN!', 'Hartnäckig!', 'Okay...'],
  dramatic: ['Spenden', '*tot*', 'Lebt!'],
  guiltTrip: ['Bitte?', 'Server...', 'Familie!', 'Danke!'],
  enthusiastic: ['Hey!', 'HEY!!', 'HEYYY!!!', 'YEAH!'],
};

const CLICKS_REQUIRED: Record<BehaviorType, number> = {
  escaping: 4,
  shrinking: 4,
  reverse: 4,
  dramatic: 3,
  guiltTrip: 4,
  enthusiastic: 4,
};

interface DonateSidebarButtonProps {
  collapsed?: boolean;
  isMobileOpen?: boolean;
}

function DonateSidebarButton({ collapsed, isMobileOpen }: DonateSidebarButtonProps): React.ReactElement {
  const [behavior] = useState<BehaviorType>(() => getRandomBehavior());
  const [clickCount, setClickCount] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [offsetY, setOffsetY] = useState(0);
  const [scale, setScale] = useState(1);
  const [isDead, setIsDead] = useState(false);

  const showLabel = !collapsed || isMobileOpen;
  const currentText = BEHAVIOR_TEXTS[behavior][Math.min(clickCount, BEHAVIOR_TEXTS[behavior].length - 1)];
  const maxClicks = CLICKS_REQUIRED[behavior];

  const handleClick = useCallback(() => {
    const newCount = clickCount + 1;

    if (newCount >= maxClicks) {
      setIsModalOpen(true);
      return;
    }

    setClickCount(newCount);

    switch (behavior) {
      case 'escaping':
        setOffsetY(Math.random() > 0.5 ? -20 : 20);
        setTimeout(() => setOffsetY(0), 300);
        break;
      case 'shrinking':
        setScale(Math.max(0.6, scale - 0.1));
        break;
      case 'dramatic':
        if (newCount === 1) {
          setIsDead(true);
          setTimeout(() => {
            setIsDead(false);
            setClickCount(2);
          }, 1500);
        }
        break;
    }
  }, [behavior, clickCount, maxClicks, scale]);

  const getIcon = () => {
    if (behavior === 'guiltTrip') {
      return Heart;
    }
    if (behavior === 'enthusiastic' && clickCount > 1) {
      return Sparkles;
    }
    return Coffee;
  };

  const Icon = getIcon();

  return (
    <>
      <motion.button
        onClick={handleClick}
        disabled={isDead}
        animate={{
          y: offsetY,
          scale: behavior === 'shrinking' ? scale : 1,
          rotate: isDead ? 45 : 0,
          opacity: isDead ? 0.4 : 1,
        }}
        whileHover={{ scale: scale * 1.02 }}
        whileTap={{ scale: scale * 0.98 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className={cn(
          'w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-colors',
          'touch-manipulation min-h-[44px]',
          behavior === 'guiltTrip'
            ? 'bg-destructive/10 hover:bg-destructive/20 text-destructive'
            : behavior === 'enthusiastic' && clickCount > 1
            ? 'bg-gradient-to-r from-primary/20 via-accent/20 to-secondary/20'
            : 'bg-primary/10 hover:bg-primary/20',
          collapsed && !isMobileOpen && 'justify-center px-2'
        )}
      >
        <motion.div
          animate={behavior === 'enthusiastic' && clickCount > 1 ? { rotate: [0, 10, -10, 0] } : {}}
          transition={{ repeat: Infinity, duration: 0.5 }}
          className={cn(
            'flex-shrink-0 p-1.5 rounded-lg',
            behavior === 'guiltTrip' ? 'text-destructive' : 'text-primary'
          )}
        >
          <Icon className="h-5 w-5" />
        </motion.div>

        <AnimatePresence mode="wait">
          {showLabel && (
            <motion.div
              initial={{ opacity: 0, width: 0 }}
              animate={{ opacity: 1, width: 'auto' }}
              exit={{ opacity: 0, width: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden text-left"
            >
              <motion.span
                key={currentText}
                initial={{ opacity: 0, y: -5 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 5 }}
                className="text-sm font-medium text-foreground whitespace-nowrap"
              >
                {currentText}
              </motion.span>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>

      <DonateModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </>
  );
}

export default DonateSidebarButton;
